import { createMongoAbility, type RawRuleOf } from '@casl/ability';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getAuthAdapter } from '../auth/createAuthAdapter';
import { fetchMyPermissions } from '../auth/fetchMyPermissions';
import type { User } from '../auth/types';
import { rotateSessionId } from '../lib/correlation';
import type { AppAbility, FrontendRule } from '../types/ability';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  // Raw rules from the backend. Storing rules (plain data) rather than the
  // built MongoAbility instance keeps the Zustand state JSON-friendly —
  // readable in devtools and trivially serialisable. The ability instance
  // is derived on read via useAbility() below.
  rules: FrontendRule[] | null;
}

interface AuthActions {
  _setUser: (user: User | null) => void;
  _setLoading: (loading: boolean) => void;
  _setRules: (rules: FrontendRule[] | null) => void;
  initiateLogin: () => Promise<void>;
  handleAuthCallback: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      user: null,
      // Start true so ProtectedRoute waits for the session check before rendering.
      isLoading: true,
      rules: null,

      _setUser: (user) => set({ user }, false, 'auth/setUser'),
      _setLoading: (isLoading) => set({ isLoading }, false, 'auth/setLoading'),
      _setRules: (rules) => set({ rules }, false, 'auth/setRules'),

      initiateLogin: async () => {
        await getAuthAdapter().initiateLogin();
      },

      handleAuthCallback: async (code: string) => {
        const session = await getAuthAdapter().exchangeCode(code);
        set({ user: session.user }, false, 'auth/handleAuthCallback');
        // Permissions fetch is best-effort — a failure shouldn't undo the login
        // (the user is already authenticated). Rules stay null and `<Can>`
        // defaults to deny, which fails safe.
        try {
          const rules = await fetchMyPermissions();
          set({ rules }, false, 'auth/setRules');
        } catch (error) {
          console.error('Failed to fetch permissions after login:', error);
        }
      },

      // IMPORTANT: broadcastLogout() must fire BEFORE adapter.logout() awaits.
      // If the API returns a SAML SLO URL the adapter hard-navigates before
      // returning, so any code after the await never runs — this also means
      // rotateSessionId() and set({ user: null }) below may be skipped on the
      // SAML SLO path. That's acceptable because the next page load
      // re-initializes the module-scope sessionId and auth state from scratch.
      logout: async () => {
        getAuthAdapter().broadcastLogout();
        await getAuthAdapter().logout();
        // Mint a fresh session ID so the next user on a shared device doesn't
        // inherit the previous session's `x-session-id` in telemetry.
        rotateSessionId();
        // Single set() clears user and rules together so subscribers never see
        // a transient state where user is null but rules still hold the prior
        // user's permissions.
        set({ user: null, rules: null }, false, 'auth/logout');
      },

      refresh: async () => {
        await getAuthAdapter().refresh();
      },
    }),
    { name: 'authStore', enabled: !import.meta.env.PROD },
  ),
);

// Module-level cache keyed on rules reference. Zustand returns stable refs
// for unchanged state, so all components calling useAbility() in the same
// render get the same MongoAbility instance. WeakMap lets the cached
// instance get garbage-collected once authStore swaps the rules array.
const ABILITY_CACHE = new WeakMap<FrontendRule[], AppAbility>();

function getAbilityFor(rules: FrontendRule[]): AppAbility {
  let ability = ABILITY_CACHE.get(rules);
  if (!ability) {
    ability = createMongoAbility(rules as unknown as RawRuleOf<AppAbility>[]) as AppAbility;
    ABILITY_CACHE.set(rules, ability);
  }
  return ability;
}

// Public selector hook. Derives the MongoAbility from the raw rules in the
// store. Returns null when no rules have been fetched yet.
export function useAbility(): AppAbility | null {
  const rules = useAuthStore((s) => s.rules);
  return rules ? getAbilityFor(rules) : null;
}
